/**
 * Form share links DEV-ONLY harness — a fake for the two form-share routes, and a
 * stand-in for the two things a real share needs that dev data may not have: a
 * leader account and a form opted in with Strapi's `leadersCanShare`.
 *
 * `form-share-api.js` routes each call here while the harness is on, so every
 * share and shared-link case can be clicked through on web, a simulator or an
 * emulator before the API is deployed or the Strapi field exists.
 *
 * Inert unless switched on from /form-share-states (localStorage), and never
 * active in a production build. Mirrors src/utils/bf-pledge-harness.js.
 */

const FORM_SHARE_HARNESS_KEY = 'hogc_form_share_harness';

// Shown in the frame when no real form has been picked for a shared link.
const PLACEHOLDER_FORM_URL = 'https://example.com/';

const defaultState = () => ({
  enabled: false,
  // null: use the account's real leader status.
  asLeader: null,
  // A form title from the member's own list to treat as `leadersCanShare`.
  optInForm: null,
  // 'ok' | 'error'
  shareOutcome: 'ok',
  // 'ok' | 'refused'
  sharedOutcome: 'ok',
  // The URL a shared form opens.
  sharedFormLink: PLACEHOLDER_FORM_URL,
  latencyMs: 400,
});

const readState = () => {
  try {
    const raw = localStorage.getItem(FORM_SHARE_HARNESS_KEY);
    return raw ? { ...defaultState(), ...JSON.parse(raw) } : defaultState();
  } catch {
    return defaultState();
  }
};

const writeState = state => {
  try {
    localStorage.setItem(FORM_SHARE_HARNESS_KEY, JSON.stringify(state));
  } catch {
    // Storage unavailable — the harness simply stays as it was.
  }
  return state;
};

export const isFormShareHarnessActive = () =>
  process.env.VUE_APP_NODE_ENV !== 'production' && readState().enabled;

export const getFormShareHarnessState = () => readState();

export const setFormShareHarness = patch => writeState({ ...readState(), ...patch });

export const resetFormShareHarness = () => writeState(defaultState());

// The API's own slug rule for a form name, so the fake link reads like the real one.
const slugFor = formTitle =>
  formTitle
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

// Shaped like axios's errors, so callers handle real and fake failures alike.
const httpError = status => {
  const error = new Error(`Request failed with status code ${status}`);
  error.response = { status, data: {} };
  return error;
};

const wait = async () => {
  const { latencyMs } = readState();
  if (latencyMs) await new Promise(resolve => setTimeout(resolve, latencyMs));
};

export const fakeFormShareApi = {
  async createShareLink(formTitle) {
    await wait();
    if (readState().shareOutcome === 'error') throw httpError(500);
    return `https://hogc.one/${slugFor(formTitle)}`;
  },

  async getSharedForm(formTitle) {
    await wait();
    const { sharedOutcome, sharedFormLink } = readState();
    if (sharedOutcome === 'refused') throw httpError(404);
    return { title: formTitle, link: sharedFormLink, leadersCanShare: true, prepopulation: {} };
  },
};

/**
 * What the harness says about sharing this form, over the real values: leader
 * status when `asLeader` is set, and the opt-in when the form is `optInForm`.
 */
export const harnessShareOverrides = ({ isLeader, formTitle, leadersCanShare }) => {
  const { asLeader, optInForm } = readState();
  return {
    isLeader: asLeader ?? isLeader,
    leadersCanShare: leadersCanShare || optInForm === formTitle,
  };
};
